import { isEven } from '@doltech/utils/lib/number';
import * as React from 'react';
import styled from 'styled-components';

import Avatar from '../Avatar/Avatar';
import { figmaColors } from '../colors';
import { colorsV2 } from '../colors-v2';

interface MessageProps {
  characterIndex: number;
  character: {
    name: string;
    avatarUrl: string;
  };
  data: {
    start: number;
    end: number;
    text: string;
  };
  isHighlight?: boolean;
}

const Message = (props: MessageProps) => {
  const {
    characterIndex,
    data: { text },
    character,
    isHighlight,
  } = props;
  const ref = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (isHighlight) {
      ref.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [isHighlight, ref]);

  const renderMessage = React.useCallback(() => {
    if (isEven(characterIndex)) {
      return (
        <Container className="left-message">
          <AvatarContainer
            className="character-avatar"
            user={{
              avatar: {
                src: character?.avatarUrl,
                size: 24,
              },
            }}
          />
          <div>
            <CharacterNameContainer>{character?.name}</CharacterNameContainer>
            <MessageContainer className={isHighlight ? 'message highlight' : 'message'}>
              {text}
            </MessageContainer>
          </div>
        </Container>
      );
    }
    return (
      <Container className="right-message">
        <div />
        <div className="message-row-right">
          <CharacterNameContainer>{character?.name}</CharacterNameContainer>
          <MessageContainer className={isHighlight ? 'message highlight' : 'message'}>
            {text}
          </MessageContainer>
        </div>
        <AvatarContainer
          className="character-avatar"
          user={{
            avatar: {
              src: character?.avatarUrl,
              size: 24,
            },
          }}
        />
      </Container>
    );
  }, [character?.avatarUrl, character?.name, characterIndex, isHighlight, text]);

  return <ConversationContainer ref={ref}>{renderMessage()}</ConversationContainer>;
};

const ConversationContainer = styled.div`
  .left-message {
    grid-template-columns: 24px 5fr 1fr;
    .message {
      background: ${figmaColors.bg5};
      color: ${colorsV2.black100};
      border-radius: 16px 16px 16px 4px;
    }
    .highlight {
      font-weight: 500;
      color: ${colorsV2.white100};
      background-color: ${colorsV2.blue80};
    }
  }
  .right-message {
    grid-template-columns: 1fr 5fr 24px;
    .message {
      background: ${figmaColors.bg5};
      color: ${colorsV2.black100};
      border-radius: 16px 16px 4px;
    }
    .time {
      justify-self: end;
    }
    .highlight {
      font-weight: 500;
      color: ${colorsV2.white100};
      background-color: ${colorsV2.blue80};
    }
  }
`;

const Container = styled.div`
  display: grid;
  column-gap: 14px;
  grid-auto-flow: column;

  .message-row-right {
    justify-self: right;
  }
`;

const MessageContainer = styled.div`
  font-size: 16px;
  line-height: 24px;
  padding: 6px 12px;
  align-self: center;
  width: fit-content;
`;

const AvatarContainer = styled(Avatar)`
  width: 24px;
  padding: 0;
  align-self: flex-end;
`;

const CharacterNameContainer = styled.div`
  font-size: 12px;
  color: ${figmaColors.text6};
  line-height: 16px;
  margin-bottom: 4px;
  width: fit-content;
`;

export default React.memo(Message);
